import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [count, setCount] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => setCount(res.data.count || 0))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/waitlist`, { email: email.trim() });
      setStatus("done");
      if (res.data.status === "exists") {
        toast("You're already on the list.");
      } else {
        setCount((c) => c + 1);
        toast.success("Welcome to the label.");
      }
    } catch {
      setStatus("idle");
      toast.error("Something went wrong — please try again.");
    }
  };

  return (
    <section
      id="waitlist"
      className="border-t border-[#26221F]/15 bg-[#26221F] px-6 py-24 text-[#FAF7F2] lg:px-12 lg:py-32"
      data-testid="waitlist-section"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.4em] text-[#D8AFA4]">
            Coming Soon
          </p>
          <h2
            className="font-display text-[13vw] leading-[0.9] tracking-tight sm:text-7xl lg:text-[8rem]"
            data-testid="waitlist-heading"
          >
            Join the <em className="italic text-[#D8AFA4]">list</em>
          </h2>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-[#FAF7F2]/70 md:text-lg">
            Be first to know when the debut collection drops — plus early access
            to bespoke designs.
            {count > 0 && (
              <span className="mt-2 block text-sm" data-testid="waitlist-count">
                {count} {count === 1 ? "woman is" : "women are"} already waiting.
              </span>
            )}
          </p>
          {status === "done" ? (
            <div
              className="mt-12 flex items-center gap-4 border border-[#FAF7F2]/25 p-6"
              data-testid="waitlist-success"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D8AFA4]">
                <Check className="h-5 w-5 text-[#26221F]" />
              </span>
              <p className="font-display text-xl italic md:text-2xl">
                You're on the list — welcome to the label.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 max-w-xl" data-testid="waitlist-form">
              <div className="flex items-center gap-4 border-b-2 border-[#FAF7F2]/40 pb-4 transition-colors duration-300 focus-within:border-[#D8AFA4]">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  aria-label="Email address for the waitlist"
                  data-testid="waitlist-email-input"
                  className="w-full bg-transparent font-display text-xl italic text-[#FAF7F2] placeholder:text-[#FAF7F2]/35 focus:outline-none md:text-2xl"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  aria-label="Join the waitlist"
                  data-testid="waitlist-submit-button"
                  className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#D8AFA4] transition-colors duration-300 hover:bg-[#FAF7F2] disabled:opacity-50"
                >
                  <ArrowRight className="h-5 w-5 text-[#26221F] transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
              <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FAF7F2]/40">
                No noise. One email when we launch.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
